import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import {
  Button,
  Field,
  SelectField,
  StatePanel,
  StatusBadge,
} from "../../components/ui";
import {
  businessApi,
  type BusinessRecord,
  type Relationship,
  type Preparation,
  type Write,
  type AttentionPage,
  type PipelineCount,
  type BusinessReports,
} from "../../data/crm-business";
import type { CaseRef, WorkspaceService } from "../../data/workspace";
import { caseUrl, copyLabel, money, serviceNames } from "./shared";
import { formatDate, formatDateTime } from "../../data/format";
import { DateBox } from "../../components/DateBox";

function useEditor<T>(
  key: string,
  load: () => Promise<BusinessRecord<T>>,
  save: (input: Write<T>) => Promise<BusinessRecord<T>>,
  onSaved?: (() => void) | undefined,
) {
  const [record, setRecord] = useState<BusinessRecord<T> | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [feedback, setFeedback] = useState("");
  const active = useRef(key);
  active.current = key;
  const loader = useRef(load);
  loader.current = load;
  const pending = useRef<Write<T> | null>(null);
  const reload = async () => {
    const target = key;
    setError("");
    setRecord(null);
    pending.current = null;
    try {
      const r = await loader.current();
      if (active.current === target) setRecord(r);
    } catch (e) {
      if (active.current === target)
        setError(
          e instanceof Error ? e.message : "No pudimos cargar la ficha.",
        );
    }
  };
  useEffect(() => {
    active.current = key;
    setFeedback("");
    setBusy(false);
    void reload();
    return () => {
      active.current = "";
    };
  }, [key]);
  const change = (data: T) => {
    if (record) {
      setRecord({ ...record, data });
      pending.current = null;
      setFeedback("");
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!record) return;
    const target = key;
    setBusy(true);
    setError("");
    setFeedback("");
    pending.current ??= {
      commandId: crypto.randomUUID(),
      expectedVersion: record.version,
      data: record.data,
    };
    try {
      const result = await save(pending.current);
      if (active.current === target) {
        setRecord(result);
        pending.current = null;
        setFeedback("Cambios guardados.");
        onSaved?.();
      }
    } catch (e) {
      if (active.current === target)
        setError(e instanceof Error ? e.message : "No pudimos guardar.");
    } finally {
      if (active.current === target) setBusy(false);
    }
  };
  return { record, error, busy, feedback, change, submit, reload };
}
export function ProfileEditor({ customerId }: { customerId: string }) {
  const { hasPermission } = useAuth();
  const editor = useEditor(
    customerId,
    () => businessApi.profile(customerId),
    (input) =>
      businessApi.saveProfile(customerId, {
        ...input,
        data: {
          ...input.data,
          tags: input.data.tags.map((s) => s.trim()).filter(Boolean),
        },
      }),
  );
  if (!editor.record)
    return (
      <StatePanel
        state={editor.error ? "error" : "loading"}
        description={editor.error || "Cargando relación con el cliente"}
        onRetry={() => void editor.reload()}
      />
    );
  const data = editor.record.data,
    change = (patch: Partial<Relationship>) =>
      editor.change({ ...data, ...patch });
  return (
    <section className="workspace-card crm-business-editor">
      <h3>Relación con el cliente</h3>
      <p>
        Preferencias e institución para próximas visitas. Cada viaje conserva su
        propio expediente.
      </p>
      <form onSubmit={editor.submit}>
        <fieldset disabled={!hasPermission("crm.write") || editor.busy}>
          <SelectField
            label="Tipo de relación"
            value={data.kind}
            onChange={(e) =>
              change({ kind: e.target.value as Relationship["kind"] })
            }
          >
            <option value="person">Persona / familia</option>
            <option value="school">Escuela</option>
            <option value="company">Empresa / agencia</option>
          </SelectField>
          <Field
            label="Institución o empresa"
            value={data.organization}
            maxLength={190}
            onChange={(e) => change({ organization: e.target.value })}
          />
          <label className="crm-textarea-field">
            <span>Preferencias para próximas visitas</span>
            <textarea
              value={data.preferences}
              maxLength={4000}
              onChange={(e) => change({ preferences: e.target.value })}
            />
          </label>
          <Field
            label="Etiquetas (separadas por comas)"
            value={data.tags.join(",")}
            onChange={(e) => change({ tags: e.target.value.split(",") })}
            placeholder="Recurrente, primaria, inglés"
          />
          <h4>Contactos adicionales</h4>
          {data.contacts.map((contact, index) => (
            <div className="crm-contact-fields" key={index}>
              {(["name", "role", "email", "phone"] as const).map((field) => (
                <Field
                  key={field}
                  label={`${{ name: "Nombre", role: "Cargo", email: "Correo", phone: "Teléfono" }[field]} del contacto ${index + 1}`}
                  required={field === "name"}
                  type={field === "email" ? "email" : "text"}
                  value={contact[field]}
                  onChange={(e) =>
                    change({
                      contacts: data.contacts.map((c, i) =>
                        i === index ? { ...c, [field]: e.target.value } : c,
                      ),
                    })
                  }
                />
              ))}
              <Button
                type="button"
                variant="quiet"
                onClick={() =>
                  change({
                    contacts: data.contacts.filter((_, i) => i !== index),
                  })
                }
              >
                Quitar contacto {index + 1}
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            disabled={data.contacts.length >= 20}
            onClick={() =>
              change({
                contacts: [
                  ...data.contacts,
                  { name: "", role: "", email: "", phone: "" },
                ],
              })
            }
          >
            Agregar contacto
          </Button>
          <footer>
            <Button type="submit">
              {editor.busy ? "Guardando…" : "Guardar relación"}
            </Button>
          </footer>
        </fieldset>
      </form>
      {editor.error && (
        <p role="alert">
          {editor.error}{" "}
          <button type="button" onClick={() => void editor.reload()}>
            Descartar cambios y recargar
          </button>
        </p>
      )}
      {editor.feedback && <p role="status">{editor.feedback}</p>}
    </section>
  );
}
const checks: Record<WorkspaceService, Array<[string, string]>> = {
  balloon: [
    ["passengers", "Nombres y edades de pasajeros completos"],
    ["weights", "Pesos y condiciones revisados"],
    ["meeting", "Horario y punto de encuentro confirmados"],
    ["weather", "Condiciones de operación revisadas"],
    ["documents", "Confirmación y documentos revisados"],
  ],
  tour: [
    ["group", "Lista de asistentes completa"],
    ["meeting", "Horario y punto de encuentro confirmados"],
    ["guide", "Guía y recorrido coordinados"],
    ["requirements", "Necesidades del grupo revisadas"],
    ["documents", "Confirmación y documentos revisados"],
  ],
  school: [
    ["group", "Alumnos, grado y acompañantes confirmados"],
    ["contacts", "Institución y contactos de emergencia revisados"],
    ["proposal", "Propuesta aceptada y modalidad acordada"],
    ["capacity", "Fecha y cupo validados en la reserva"],
    ["meeting", "Llegada y actividades coordinadas"],
    ["documents", "Autorizaciones y documentos revisados"],
  ],
};
export function PreparationEditor({
  caseRef,
  service,
  onSaved,
}: {
  caseRef: CaseRef;
  service: WorkspaceService;
  onSaved?: () => void;
}) {
  const { hasPermission } = useAuth();
  const editor = useEditor(
    `${caseRef.kind}:${caseRef.id}`,
    () => businessApi.preparation(caseRef),
    (input) => businessApi.savePreparation(caseRef, input),
    onSaved,
  );
  if (!editor.record)
    return (
      <StatePanel
        state={editor.error ? "error" : "loading"}
        description={editor.error || "Cargando preparación"}
        onRetry={() => void editor.reload()}
      />
    );
  // A case without a saved checklist yet starts with every item unchecked.
  const saved = (editor.record.data ?? {}) as Partial<Preparation>;
  const data = { ...saved, checks: saved.checks ?? {} } as Preparation,
    change = (patch: Partial<Preparation>) =>
      editor.change({ ...data, ...patch });
  return (
    <section className="workspace-card crm-business-editor">
      <h2>Preparación y cierre · {serviceNames[service]}</h2>
      <p>
        Lista de revisión del equipo. La confirmación, asistencia oficial y
        pagos se consultan en la reserva.
      </p>
      <form onSubmit={editor.submit}>
        <fieldset disabled={!hasPermission("crm.write") || editor.busy}>
          <div className="crm-checklist">
            {checks[service].map(([id, label]) => (
              <label key={id}>
                <input
                  type="checkbox"
                  checked={Boolean(data.checks[id])}
                  onChange={(e) =>
                    change({
                      checks: { ...data.checks, [id]: e.target.checked },
                    })
                  }
                />
                {label}
              </label>
            ))}
          </div>
          <label className="crm-textarea-field">
            <span>Punto de encuentro y logística</span>
            <textarea
              value={data.logistics}
              maxLength={4000}
              onChange={(e) => change({ logistics: e.target.value })}
            />
          </label>
          <label className="crm-textarea-field">
            <span>
              {service === "school"
                ? "Grado, acompañantes y necesidades del grupo"
                : "Necesidades y condiciones del servicio"}
            </span>
            <textarea
              value={data.requirements}
              maxLength={4000}
              onChange={(e) => change({ requirements: e.target.value })}
            />
          </label>
          <h3>Cierre interno del servicio</h3>
          <SelectField
            label="Resultado del servicio"
            value={data.outcome}
            onChange={(e) =>
              change({ outcome: e.target.value as Preparation["outcome"] })
            }
          >
            <option value="pending">Pendiente de revisar</option>
            <option value="completed">
              Realizado · revisado por el equipo
            </option>
            <option value="not_performed">
              No realizado · registrar motivo
            </option>
          </SelectField>
          <label className="crm-textarea-field">
            <span>Notas de cierre e incidencias</span>
            <textarea
              required={data.outcome !== "pending"}
              value={data.closingNotes}
              maxLength={4000}
              onChange={(e) => change({ closingNotes: e.target.value })}
            />
          </label>
          <h3>Seguimiento después de la visita</h3>
          <DateBox
            label="Fecha del próximo contacto"
            name="post_visit_date"
            value={data.postVisitDate ?? ""}
            onChange={(date) => change({ postVisitDate: date || null })}
          />
          <Field
            label="Acción posterior"
            required={Boolean(data.postVisitDate)}
            value={data.postVisitNote}
            maxLength={2000}
            onChange={(e) => change({ postVisitNote: e.target.value })}
            placeholder="Consultar satisfacción o preparar la próxima visita"
          />
          <SelectField
            label="Estado del contacto posterior"
            value={data.postVisitStatus}
            onChange={(e) =>
              change({
                postVisitStatus: e.target
                  .value as Preparation["postVisitStatus"],
              })
            }
          >
            <option value="pending">Pendiente</option>
            <option value="completed">Realizado</option>
          </SelectField>
          <footer>
            <Button type="submit">
              {editor.busy ? "Guardando…" : "Guardar preparación y cierre"}
            </Button>
          </footer>
        </fieldset>
      </form>
      {editor.error && (
        <p role="alert">
          {editor.error}{" "}
          <button type="button" onClick={() => void editor.reload()}>
            Descartar cambios y recargar
          </button>
        </p>
      )}
      {editor.feedback && <p role="status">{editor.feedback}</p>}
    </section>
  );
}
export const attentionLabels: Record<string, string> = {
  followup: "Seguimientos para hoy",
  no_followup: "Sin siguiente seguimiento",
  quote: "Propuestas sin respuesta",
  balance: "Saldos por revisar",
  document: "Documentos con error",
  closure: "Servicios por cerrar",
  postvisit: "Contactos posteriores",
};
const attentionSection: Record<string, string> = {
  balance: "Reserva y pagos",
  document: "Documentos",
  closure: "Preparación y cierre",
  postvisit: "Preparación y cierre",
  quote: "Cotizaciones",
};
